import React, { useEffect, useRef, useState } from 'react'
import { Button, Dimmer, Loader, Message, Modal } from 'semantic-ui-react'
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import '../../pages/calendar/style.less';
import { getServiceInstanceImage, getStandaloneSignageImage } from '../../services/ImageUploader';
import { changeNormalActivityAttendingStatus, changeServiceActivityAttendingStatus, deleteServiceInstanceAttendee, deleteActivityAttendee, fetchActivityAttendeeById } from '../../services/Calendar';
import sendToast from '../../../utils/Toast';
import { fetchByDocIds } from '../../services/Documents';
import { Document } from '../../types/Document';
import { fetchSvcInstanceAttendee } from '../../../services/activityAttendees';
import OrderCommentsSection from '../OrderStatus/OrderCommentsSection';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { CloseIcon } from '../icons';
import { deleteCalendarEvent } from '../../services/Calendar';
import { formatChargeCents } from '../ChargeBreakdown';
import useNeighborsPaymentAccess from '../../hooks/useNeighborsPaymentAccess';
import useResidentOrderCardEligibility from '../../hooks/useResidentOrderCardEligibility';
import { CardGateReason, getCardGateActionLabel, getCardGateMessage, resolveCardGateReason } from '../../utils/orderCardGate';
import '../orderCardGate.less';

type IconColor = "green" | "blue" | "red" | "orange" | "yellow" | "olive" | "teal" | "violet" | "purple" | "pink" | "brown" | "grey" | "black" | undefined;

const parseQAPairs = (text: string): { header: string; pairs: { question: string; answer: string }[]; extras: string[] } => {
    if (!text) return { header: '', pairs: [], extras: [] };

    const segments = text.split(/\.\s*/).filter(s => s.trim());
    const pairs: { question: string; answer: string }[] = [];
    const extras: string[] = [];
    let header = '';

    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (segment.includes('?')) {
            const qaParts = segment.split('?');
            const question = qaParts[0].trim() + '?';
            const answer = qaParts.slice(1).join('?').trim();
            if (answer) {
                pairs.push({ question, answer });
            } else if (i + 1 < segments.length && !segments[i + 1].includes('?')) {
                pairs.push({ question, answer: segments[i + 1].trim() });
                i++;
            }
        } else if (!header) {
            header = segment.trim();
        } else {
            extras.push(segment.trim());
        }
    }

    return { header, pairs, extras };
};

const parseEventMoment = (date: string, startTime: string) =>
    moment(`${date} ${startTime}`, ['M/D/YYYY hh:mm A', 'M/D/YYYY h:mm A']);

const formatFacilityDateTime = (date: string, startTime: string) => {
    const parsed = parseEventMoment(date, startTime);
    if (!parsed.isValid()) return `${startTime}, ${date}`;
    return `${parsed.format('h:mm A')}, ${parsed.format('MMMM D, YYYY')}`;
};

const formatResidentDate = (date: string, startTime: string) => {
    const parsed = parseEventMoment(date, startTime);
    if (!parsed.isValid()) return date;
    return parsed.format('dddd, MMMM D');
};

const formatResidentTimeRange = (startTime: string, endTime?: string, durationMinutes?: number) => {
    const start = moment(startTime, ['hh:mm A', 'h:mm A']);
    const startLabel = start.isValid() ? start.format('h:mm A') : startTime;
    if (endTime) {
        const end = moment(endTime, ['hh:mm A', 'h:mm A']);
        const endLabel = end.isValid() ? end.format('h:mm A') : endTime;
        if (durationMinutes && durationMinutes > 0) {
            return `${startLabel} – ${endLabel} (${durationMinutes} min)`;
        }
        return `${startLabel} – ${endLabel}`;
    }
    return startLabel;
};

const CalendarIcon = ({ size = 17 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const PinIcon = ({ size = 17 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

const TagIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
);

const CheckIcon = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ClockIcon = ({ size = 20 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const XCircleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
);

const BannerPlaceholderIcon = () => (
    <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v9m0 0l-3-3m3 3l3-3" />
    </svg>
);

const SelectedCalendarEventModal = ({
    closeSelectedEventModal,
    openSelectedEventModal,
    selectedEventInfo,
    refreshCalendarEvents,
    setRefreshCalendarEvents,
    facilityTimezone,
    onEditResidentEvent,
}) => {
    const [imageUrl, setImageUrl] = useState("");
    // F18: what attending is estimated to charge (cost + required add-ons + tax); externalCost alone is pre-tax.
    const estimatedCharge = selectedEventInfo.estimatedCharge;
    const estimatedChargeLabel = estimatedCharge && estimatedCharge.totalCents > 0
        ? formatChargeCents(estimatedCharge.totalCents, estimatedCharge.currency)
        : '';
    // Registering for an event that will be charged needs a default card, same as orders.
    // Live only attaches estimatedCharge when facility card payments are on and the event is payable.
    const history = useHistory();
    const { resolvedFacilityPaymentsEnabled, facilityLookupFailed, retry: retryFacilityPayments } = useNeighborsPaymentAccess();
    const registrationPaymentRequired = Boolean(resolvedFacilityPaymentsEnabled && estimatedCharge && estimatedCharge.totalCents > 0);
    const {
        loading: cardEligibilityLoading,
        hasDefaultCard,
        hasAnyCard,
        defaultCardExpired,
        lookupFailed,
        refresh: refreshCardEligibility,
    } = useResidentOrderCardEligibility({
        residentId: String(selectedEventInfo.registrantId || ''),
        facilityId: String(selectedEventInfo.Facility || ''),
        enabled: registrationPaymentRequired,
    });
    const registrationCardGateReason: CardGateReason = registrationPaymentRequired
        ? resolveCardGateReason({ loading: cardEligibilityLoading, lookupFailed: lookupFailed || facilityLookupFailed, hasDefaultCard, hasAnyCard, defaultCardExpired })
        : null;
    const handleRetryCardEligibility = async () => {
        if (facilityLookupFailed) retryFacilityPayments();
        await refreshCardEligibility();
    };
    const estimatedChargeDetail = estimatedChargeLabel && estimatedCharge && estimatedCharge.taxCents > 0
        ? `incl. ${formatChargeCents(estimatedCharge.taxCents, estimatedCharge.currency)} tax`
        : '';
    const [isEventLoading, setIsEventLoading] = useState<boolean>(true)
    const [isAttendanceActionLoading, setIsAttendanceActionLoading] = useState<boolean>(false);
    const [colorOfIcon, setColorOfIcon] = useState<IconColor>("green")
    const [attendingStatus, setAttendingStatus] = useState<string>("Attending")
    const [popUpContent, setPopUpContent] = useState<string>("")
    const isMounted = useRef(false);
    const [attachedDocs, setAttachedDocs] = useState<Document[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeletingEvent, setIsDeletingEvent] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        setAttachedDocs([]);
        const fetchImage = async () => {
            try {
                setIsEventLoading(true);
                let image: string = "";
                setImageUrl("");
                if (selectedEventInfo.typeOfRequest === "service" && selectedEventInfo.image) {
                    image = await getServiceInstanceImage({
                        facilityId: selectedEventInfo.Facility,
                        svcInstanceId: selectedEventInfo.svcInstanceId,
                        fileName: selectedEventInfo.image,
                    });
                } else if (selectedEventInfo.typeOfRequest === "normal" && selectedEventInfo.contentId) {
                    image = await getStandaloneSignageImage({
                        facilityId: selectedEventInfo.Facility,
                        contentType: "activities",
                        contentId: selectedEventInfo.contentId,
                    });
                } else {
                    image = selectedEventInfo.image || "";
                }
                if (isMounted.current) {
                    setImageUrl(image);
                }
            } catch (error) {
                console.log(error);
                sendToast("error", "Error fetching image")
            } finally {
                if (isMounted.current) {
                    setIsEventLoading(false);
                }
            }
        };

        const fetchByDocs = async () => {
            try {
                setIsEventLoading(true);
                const docIds = Array.isArray(selectedEventInfo.docIds) ? selectedEventInfo.docIds : [];
                if (docIds.length > 0) {
                    const { Result: docs } = await fetchByDocIds(selectedEventInfo.Facility, docIds);
                    setAttachedDocs(Array.isArray(docs) ? docs : []);
                } else {
                    setAttachedDocs([]);
                }
            } catch (error) {
                sendToast("error", `Error fetching attached document`);
            } finally {
                if (isMounted.current) {
                    setIsEventLoading(false);
                }
            }
        }

        const getEventAttendingStatus = () => {
            const { attendingStatus, showAttendance } = selectedEventInfo;
            const status = attendingStatus.status
                ? attendingStatus.status.toLowerCase()
                : attendingStatus.toLowerCase();
            switch (status) {
                case 'notattending':
                    setColorOfIcon("blue");
                    setAttendingStatus("Not Attending");
                    if (showAttendance ) {
                        setPopUpContent("Register for the event");
                    } else {
                        setPopUpContent("Attendance is not allowed for the event");
                    }
                    break;
                case 'intend_attend':
                    setColorOfIcon("green");
                    setPopUpContent("You are registered for the event, (Click to unregister)");
                    setAttendingStatus("Attending");
                    break;
                case 'attended':
                    setColorOfIcon("green");
                    setAttendingStatus("Attended");
                    break;
                case 'waiting':
                    setColorOfIcon("orange");
                    setPopUpContent("You are in waiting list for the event, (Click to leave waiting list)");
                    setAttendingStatus("Waiting List");
                    break;
                default:
                    setColorOfIcon("red");
                    setAttendingStatus("Declined");
                    break;
            }
        }
        if (selectedEventInfo) {
            fetchImage();
        }
        if (selectedEventInfo && selectedEventInfo.attendingStatus) {
            getEventAttendingStatus();
        }
        if (selectedEventInfo && Array.isArray(selectedEventInfo.docIds) && selectedEventInfo.docIds.length) {
            fetchByDocs()
        }
        return () => {
            isMounted.current = false;
        };
    }, [selectedEventInfo]);

    const handleDeleteResidentEvent = async () => {
        if (isDeletingEvent || !selectedEventInfo.attendingId || !selectedEventInfo.Facility) return;
        try {
            setIsDeletingEvent(true);
            const res = await deleteCalendarEvent({
                facilityId: selectedEventInfo.Facility,
                payload: { _id: String(selectedEventInfo.attendingId) },
            });
            if (res) {
                sendToast('success', 'Event deleted successfully');
                setShowDeleteConfirm(false);
                closeSelectedEventModal();
                setRefreshCalendarEvents(!refreshCalendarEvents);
            } else {
                sendToast('error', 'Error deleting event');
            }
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error deleting event');
        } finally {
            setIsDeletingEvent(false);
        }
    };

    const handleChangeAttendingStatus = async () => {
        if (isAttendanceActionLoading) return;

        setIsAttendanceActionLoading(true);
        try {
            if (attendingStatus === "Not Attending" && selectedEventInfo.showAttendance) {
                if (registrationCardGateReason) {
                    sendToast("warn", getCardGateMessage(registrationCardGateReason, 'event'));
                    return;
                }
                const payload = {
                    Facility: selectedEventInfo.Facility,
                    status: "intend_attend", // change the status to intend_attend from notattending so that resident can get registered for the event
                    registrantId: selectedEventInfo.registrantId,
                    registrant: selectedEventInfo.registrant,
                };
                try {
                    setIsEventLoading(true);
                    let res;
                    if (selectedEventInfo.typeOfRequest === "service") {
                        payload["svcInstanceId"] = selectedEventInfo.attendingId;
                        payload["svcType"] = "activity";
                        payload["isLockRegistrationCheckRequired"] = true
                        res = await changeServiceActivityAttendingStatus({ facilityId: selectedEventInfo.Facility, payload });
                    } else if (selectedEventInfo.typeOfRequest === "normal") {
                        payload["activityId"] = selectedEventInfo.attendingId;
                        res = await changeNormalActivityAttendingStatus({ facilityId: selectedEventInfo.Facility, payload });
                    }
                    const buildRegistrationMsg = () => {
                        const costStr = estimatedChargeLabel || selectedEventInfo.externalCost
                            ? ` for ${estimatedChargeLabel || selectedEventInfo.externalCost}`
                            : '';
                        return `You are registered for ${selectedEventInfo.title} at ${selectedEventInfo.startTime} ${selectedEventInfo.date}${costStr}`;
                    };
                    if (selectedEventInfo.typeOfRequest === "service" && res && res.Content && res.Content.status) {
                        const status = res.Content.status;
                        let toastMsg: string;
                        let toastType: "success" | "warn" = "success";
                        if (status === "maxed") {
                            // The backend refused the registration (event full/closed) and saved nothing.
                            // Report that as-is: don't flip the local status or show the success toast.
                            sendToast(
                                "warn",
                                res.Content.message || "Registration for this event is closed. Please contact your community for more details.",
                            );
                            closeSelectedEventModal();
                            // Refresh so the calendar reflects the event's real state.
                            setRefreshCalendarEvents(!refreshCalendarEvents);
                            return;
                        }
                        if (status === "waiting") {
                            // The backend message names the resident ("Resident AL KALEESE has been added...").
                            // The resident is the one reading this, so use a fixed first-person message.
                            // A waitlist spot is not a confirmed registration, so use the yellow warn toast.
                            toastMsg = "You have been added to the waiting list";
                            toastType = "warn";
                            setAttendingStatus("Waiting List");
                            setColorOfIcon("orange");
                        } else {
                            toastMsg = buildRegistrationMsg();
                            setAttendingStatus("Attending");
                            setColorOfIcon("green");
                        }
                        sendToast(toastType, toastMsg);
                        closeSelectedEventModal();
                        setRefreshCalendarEvents(!refreshCalendarEvents);
                    } else if (selectedEventInfo.typeOfRequest === "normal" && res && res.Content) {
                        setAttendingStatus("Attending");
                        setColorOfIcon("green");
                        sendToast("success", buildRegistrationMsg());
                        closeSelectedEventModal();
                        setRefreshCalendarEvents(!refreshCalendarEvents);
                    }
                } catch (error) {
                    console.log(error);
                    sendToast("error", error instanceof Error ? error.message : "Error changing attending status");
                } finally {
                    setIsEventLoading(false);
                }
            } else if(selectedEventInfo.showAttendance && (attendingStatus === "Attending" || attendingStatus === "Waiting List")) {
                let attendeeData: any = {};
                if (selectedEventInfo) {
                    console.log("selectedEventInfo", selectedEventInfo);
                    const { registrantId, Facility, attendingId: activityId } = selectedEventInfo;
                    // Attendance rows are stored against this occurrence's _id (attendingId), the same id used
                    // when registering. svcInstanceId is the recurring series srcId and would match nothing.
                    if (selectedEventInfo.typeOfRequest === "service" && activityId) {
                        const data = await fetchSvcInstanceAttendee(Facility, activityId, registrantId);
                        attendeeData = data.Result && data.Result[0];
                    } else if (selectedEventInfo.typeOfRequest === "normal" && activityId) {
                        const data = await fetchActivityAttendeeById(Facility, activityId, registrantId);
                        attendeeData = data.Result && data.Result[0];
                    }
                }
                try {
                    setIsEventLoading(true);
                    if (!attendeeData) {
                        sendToast("error", "Could not find your registration for this event. Please refresh and try again.");
                        return;
                    }
                    let res;
                    if (selectedEventInfo.typeOfRequest === "service" && attendeeData) {
                        const payload = {
                            svcInstanceId: attendeeData.svcInstanceId,
                            svcType: "activity",
                            _id: String(attendeeData._id)
                        };
                        res = await deleteServiceInstanceAttendee({ facilityId: selectedEventInfo.Facility, payload });
                    } else if (selectedEventInfo.typeOfRequest === "normal" && attendeeData) {
                        const payload = {
                            _id: String(attendeeData._id),
                            activityId: attendeeData.activityId
                        };
                        res = await deleteActivityAttendee({ facilityId: selectedEventInfo.Facility, payload });
                    }
                    if (res) {
                        setAttendingStatus("Not Attending");
                        setColorOfIcon("blue");
                        const toastMsg = (res.Content && res.Content.message)
                            ? res.Content.message
                            : "Successfully Unregistered for the event";
                        sendToast("success", toastMsg);
                        closeSelectedEventModal();
                        setRefreshCalendarEvents(!refreshCalendarEvents); // Trigger the refresh of calendar events
                    }
                } catch (error) {
                    console.log(error);
                    sendToast("error", error instanceof Error ? error.message : "Error changing attending status");
                } finally {
                    setIsEventLoading(false);
                }
            } else if(!selectedEventInfo.showAttendance) {
                sendToast("warn", "Enable registration checkbox to update attendance status");
            }
        } finally {
            setIsAttendanceActionLoading(false);
        }
    };

    // Full and not taking this resident (no waiting list, or a manual one); backend flag from the calendar list.
    const isRegistrationClosed = attendingStatus === "Not Attending" && !!selectedEventInfo.showAttendance && !!selectedEventInfo.isRegistrationClosed;

    const getAttendanceButtonLabel = () => {
        if (isRegistrationClosed) return "Registration closed";
        if (attendingStatus === "Not Attending" && selectedEventInfo.showAttendance) return "Register";
        if (attendingStatus === "Not Attending" && !selectedEventInfo.showAttendance) return "You cannot register for this event";
        if (attendingStatus === "Waiting List") return "Waiting list";
        if (attendingStatus === "Attending") return "You're registered";
        if (attendingStatus === "Attended") return "You have already attended this event";
        if (attendingStatus === "Declined") return "You've been declined for this event";
        return "";
    };

    const getAttendanceButtonClass = () => {
        if (isRegistrationClosed) return "cal-evt-register-btn waitlisted"; // yellow, same as the waiting-list state
        if (attendingStatus === "Not Attending" && selectedEventInfo.showAttendance) return "cal-evt-register-btn";
        if (attendingStatus === "Not Attending" && !selectedEventInfo.showAttendance) return "cal-evt-register-btn none";
        if (attendingStatus === "Waiting List") return "cal-evt-register-btn waitlisted";
        if (attendingStatus === "Attending") return "cal-evt-register-btn registered";
        if (attendingStatus === "Attended") return "cal-evt-register-btn registered";
        if (attendingStatus === "Declined") return "cal-evt-register-btn declined";
        return "cal-evt-register-btn";
    };

    const getStatusBanner = () => {
        if (isRegistrationClosed) {
            return { variant: "waitlisted", text: "This event is closed. Please contact your community for more details.", icon: <XCircleIcon /> };
        }
        if (attendingStatus === "Waiting List") {
            return { variant: "waitlisted", text: "You are on the waiting list", icon: <ClockIcon /> };
        }
        if (attendingStatus === "Attending") {
            return { variant: "registered", text: "You are registered for this event", icon: <CheckIcon /> };
        }
        if (attendingStatus === "Attended") {
            return { variant: "registered", text: "You attended this event", icon: <CheckIcon /> };
        }
        if (attendingStatus === "Declined") {
            return { variant: "declined", text: "Registration declined", icon: <XCircleIcon /> };
        }
        if (attendingStatus === "Not Attending" && !selectedEventInfo.showAttendance) {
            return { variant: "none", text: "No registration", icon: <XCircleIcon /> };
        }
        return null;
    };

    const renderCloseButton = (className: string) => (
        <button
            type="button"
            className={className}
            onClick={closeSelectedEventModal}
            aria-label="Close"
        >
            <CloseIcon size={18} />
        </button>
    );

    const renderAttachedDocs = () => {
        if (!attachedDocs.length) return null;
        const docsWithLinks = attachedDocs.filter((doc) => doc?.attachedFiles?.[0]?.attachedLink);
        if (!docsWithLinks.length) return null;
        return (
            <div className="cal-evt-docs">
                <div className="cal-evt-long-label">Attached documents</div>
                <div className="cal-evt-docs-list">
                    {docsWithLinks.map((doc, index) => (
                        <a
                            key={index}
                            href={doc.attachedFiles?.[0]?.attachedLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cal-evt-doc-link"
                        >
                            {doc.Name}
                        </a>
                    ))}
                </div>
            </div>
        );
    };

    const renderQAContent = (pairs: { question: string; answer: string }[], extras: string[]) => {
        if (!pairs.length && !extras.length) return null;
        return (
            <div className="cal-evt-qa">
                {pairs.map((pair, index) => (
                    <div key={index} className="cal-evt-qa-pair">
                        <div className="cal-evt-qa-label">{pair.question}</div>
                        <div className="cal-evt-qa-value">{pair.answer}</div>
                    </div>
                ))}
                {extras.length > 0 && (
                    <div className="cal-evt-qa-extras">
                        <div className="cal-evt-qa-label">Additional responses</div>
                        {extras.map((extra, index) => (
                            <div key={index} className="cal-evt-qa-extra">{extra}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderAttendanceAction = () => {
        if (selectedEventInfo.typeOfRequest === "resident") return null;
        const isRegistered = attendingStatus === "Attending";
        const isAttended = attendingStatus === "Attended";
        const isDeclined = attendingStatus === "Declined";
        const isWaitlisted = attendingStatus === "Waiting List";
        const cannotRegister = attendingStatus === "Not Attending" && !selectedEventInfo.showAttendance;
        const canRegister = attendingStatus === "Not Attending" && selectedEventInfo.showAttendance && !isRegistrationClosed;
        const canChangeStatus = selectedEventInfo.showAttendance && (isRegistered || isWaitlisted);
        if (!isRegistered && !isAttended && !isDeclined && !isWaitlisted && !canRegister && !cannotRegister && !isRegistrationClosed) return null;
        const statusLabel = getAttendanceButtonLabel();
        if (!statusLabel) return null;
        const actionLabel = isRegistered ? "Unregister" : isWaitlisted ? "Leave waiting list" : "";
        return (
            <div className="cal-evt-attendance">
                {canRegister && registrationCardGateReason && (
                    <Message warning className="orderCardGateNotice">
                        <Message.Header>Card Required For Paid Event</Message.Header>
                        <p>{getCardGateMessage(registrationCardGateReason, 'event')}</p>
                        <div className="orderCardGateActions">
                            {registrationCardGateReason !== 'checking' && (
                                <Button type="button" size="small" primary onClick={() => history.push('/payments')}>
                                    {getCardGateActionLabel(registrationCardGateReason)}
                                </Button>
                            )}
                            <Button type="button" size="small" onClick={handleRetryCardEligibility} loading={cardEligibilityLoading} disabled={cardEligibilityLoading}>
                                Retry
                            </Button>
                        </div>
                    </Message>
                )}
                {canRegister ? (
                    <button
                        type="button"
                        className="cal-evt-register-btn"
                        disabled={isAttendanceActionLoading || isEventLoading || Boolean(registrationCardGateReason)}
                        onClick={handleChangeAttendingStatus}
                    >
                        Register
                    </button>
                ) : (
                    <div className={`${getAttendanceButtonClass()} cal-evt-status-chip`} role="status">
                        {(isRegistered || isAttended) && <CheckIcon size={18} />}
                        {isWaitlisted && <ClockIcon size={18} />}
                        {(isDeclined || cannotRegister || isRegistrationClosed) && <XCircleIcon />}
                        {statusLabel}
                    </div>
                )}
                {canChangeStatus && (
                    <button
                        type="button"
                        className={`cal-evt-register-btn ${isWaitlisted ? "leave-waitlist" : "unregister"}`}
                        disabled={isAttendanceActionLoading || isEventLoading}
                        onClick={handleChangeAttendingStatus}
                    >
                        {actionLabel}
                    </button>
                )}
            </div>
        );
    };

    const renderFacilityEventContent = () => {
        const isNormal = selectedEventInfo.typeOfRequest === "normal";
        const { header, pairs, extras } = isNormal ? parseQAPairs(selectedEventInfo.title) : { header: '', pairs: [], extras: [] };
        const eventName = isNormal ? (header || selectedEventInfo.title) : selectedEventInfo.title;
        const statusBanner = getStatusBanner();
        const showImage = Boolean((selectedEventInfo.image || selectedEventInfo.contentId) && imageUrl);

        return (
            <>
                <div className="cal-evt-banner">
                    {showImage ? (
                        <img src={imageUrl} alt="" onError={() => setImageUrl("")} />
                    ) : (
                        <div className="cal-evt-banner-icon">
                            <BannerPlaceholderIcon />
                        </div>
                    )}
                    {renderCloseButton("cal-evt-close")}
                </div>

                {statusBanner && (
                    <div className={`cal-evt-status ${statusBanner.variant}`}>
                        <div className="cal-evt-status-icon">{statusBanner.icon}</div>
                        <div className="cal-evt-status-text min-w-0 break-words">{statusBanner.text}</div>
                    </div>
                )}

                <div className="cal-evt-content">
                    <div className="cal-evt-name min-w-0 break-words">{eventName}</div>
                    {!isNormal && selectedEventInfo.description && (
                        <div className="cal-evt-short">{selectedEventInfo.description}</div>
                    )}

                    <div className="cal-evt-facts">
                        <div className="cal-evt-fact flex min-w-0 items-start gap-nbp-10">
                            <CalendarIcon />
                            <span className="min-w-0 break-words">{formatFacilityDateTime(selectedEventInfo.date, selectedEventInfo.startTime)}</span>
                        </div>
                        {selectedEventInfo.location && (
                            <div className="cal-evt-fact flex min-w-0 items-start gap-nbp-10">
                                <PinIcon />
                                <span className="min-w-0 break-words">{selectedEventInfo.location}</span>
                            </div>
                        )}
                        {(estimatedChargeLabel || selectedEventInfo.externalCost) && (
                            <div className="cal-evt-fact flex min-w-0 items-start gap-nbp-10">
                                <TagIcon />
                                <span className="min-w-0 break-words">
                                    Cost: {estimatedChargeLabel || selectedEventInfo.externalCost}
                                    {estimatedChargeDetail && <span className="cal-evt-cost-detail"> ({estimatedChargeDetail})</span>}
                                </span>
                            </div>
                        )}
                    </div>

                    {isNormal && renderQAContent(pairs, extras)}

                    {!isNormal && selectedEventInfo.longDescription && (
                        <>
                            <div className="cal-evt-long-label">About this event</div>
                            <div className="cal-evt-long">{selectedEventInfo.longDescription}</div>
                        </>
                    )}

                    {renderAttachedDocs()}
                    {renderAttendanceAction()}

                    {selectedEventInfo.typeOfRequest === "service"
                        && selectedEventInfo.attendingId
                        && Boolean(selectedEventInfo.commentsEnabled) && (
                        <div className="cal-evt-comments calendarCommentsCompact">
                            <OrderCommentsSection
                                commentsEnabled={Boolean(selectedEventInfo.commentsEnabled)}
                                variant="calendar"
                                facilityId={String(selectedEventInfo.Facility || '')}
                                serviceInstanceId={String(selectedEventInfo.attendingId)}
                                targetType="serviceInstance"
                                registrantId={String(selectedEventInfo.registrantId || '')}
                                facilityTimezone={facilityTimezone || ''}
                            />
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderResidentEventContent = () => {
        const { header, pairs, extras } = parseQAPairs(selectedEventInfo.title);
        const eventName = pairs.length > 0 ? (header || selectedEventInfo.title) : selectedEventInfo.title;

        return (
            <>
                <div className="cal-view-header">
                    <div>
                        <div className="cal-view-badge">
                            <CalendarIcon size={12} />
                            Your Event
                        </div>
                    </div>
                    {renderCloseButton("cal-view-close")}
                </div>
                <div className="cal-view-body">
                    <div className="cal-view-name min-w-0 break-words">{eventName}</div>
                    <div className="cal-view-facts">
                        <div className="cal-view-fact">
                            <div className="cal-view-fact-icon">
                                <CalendarIcon size={16} />
                            </div>
                            <div>
                                <div className="cal-view-fact-text">
                                    {formatResidentDate(selectedEventInfo.date, selectedEventInfo.startTime)}
                                </div>
                                <div className="cal-view-fact-sub">
                                    {formatResidentTimeRange(
                                        selectedEventInfo.startTime,
                                        selectedEventInfo.endTime,
                                        selectedEventInfo.durationMinutes,
                                    )}
                                </div>
                            </div>
                        </div>
                        {selectedEventInfo.location && (
                            <div className="cal-view-fact">
                                <div className="cal-view-fact-icon">
                                    <PinIcon size={16} />
                                </div>
                                <div className="cal-view-fact-text">{selectedEventInfo.location}</div>
                            </div>
                        )}
                    </div>
                    {renderQAContent(pairs, extras)}
                    {renderAttachedDocs()}
                    <div className="cal-view-actions flex min-w-0 flex-wrap gap-nbp-10">
                        <button
                            type="button"
                            className="cal-view-btn cal-view-btn-edit min-w-0 flex-1"
                            onClick={onEditResidentEvent}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Edit
                        </button>
                        <button
                            type="button"
                            className="cal-view-btn cal-view-btn-delete min-w-0 flex-1"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                            Delete
                        </button>
                    </div>
                </div>
            </>
        );
    };

    const isResident = selectedEventInfo.typeOfRequest === "resident";

    return (
        <>
        <Modal
            open={openSelectedEventModal}
            onClose={closeSelectedEventModal}
            className={`cal-event-detail-modal${isResident ? " is-resident" : ""}`}
        >
            <Dimmer active={isEventLoading || isDeletingEvent} inverted>
                <Loader />
            </Dimmer>
            <Modal.Content className="cal-evt-shell">
                {isResident ? renderResidentEventContent() : renderFacilityEventContent()}
            </Modal.Content>
        </Modal>
        <DeleteConfirmationModal
            open={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDeleteResidentEvent}
            messageToDisplay="Are you sure you want to delete this event?"
            loading={isDeletingEvent}
        />
        </>
    );
}

export default SelectedCalendarEventModal;
